type ErrorMessageOptions = {
  fallback: string;
  status?: number;
};

function nonEmptyString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function messageFromValue(value: unknown): string | null {
  const direct = nonEmptyString(value);
  if (direct) return direct;
  if (value instanceof Error) return nonEmptyString(value.message);
  if (typeof value !== 'object' || value === null) return null;

  if ('message' in value) {
    const message = nonEmptyString(value.message);
    if (message) return message;
  }
  if ('summary' in value) {
    const summary = nonEmptyString(value.summary);
    if (summary) return summary;
  }
  if ('error' in value) {
    const nested = messageFromValue(value.error);
    if (nested) return nested;
  }
  if ('value' in value) return messageFromValue(value.value);
  return null;
}

function statusExplanation(status: number): string {
  if (status === 401) return 'Your session is no longer valid. Sign in and try again.';
  if (status === 403) return 'You do not have permission to complete this action.';
  if (status === 404) return 'The requested file or folder no longer exists.';
  if (status === 408) return 'The request timed out before it completed.';
  if (status === 413) return 'The file is larger than the server allows.';
  if (status === 429) return 'The server is receiving too many requests. Wait a moment and retry.';
  if (status >= 500) {
    return 'The server encountered an internal error. Try again; if it continues, contact an administrator.';
  }
  return `The server rejected the request (HTTP ${status}).`;
}

export function apiErrorMessage(error: unknown, options: ErrorMessageOptions): string {
  const message = messageFromValue(error);
  const status = options.status;
  const isNetworkFailure =
    error instanceof TypeError &&
    /failed to fetch|load failed|network|fetch failed/i.test(error.message);

  if (isNetworkFailure) {
    return `${options.fallback} The server could not be reached. Check your connection and try again.`;
  }

  const isGenericServerMessage =
    message !== null && /^(internal server error|request failed)$/i.test(message);
  if (message && !isGenericServerMessage) {
    return status && status >= 400 ? `${message} (HTTP ${status}).` : message;
  }
  if (isGenericServerMessage && !status) {
    return `${options.fallback} The server reported an internal error. Try again; if it continues, contact an administrator.`;
  }
  if (status && status >= 400) return `${options.fallback} ${statusExplanation(status)}`;
  return message ? `${options.fallback} ${message}` : options.fallback;
}

export async function responseErrorMessage(response: Response, fallback: string): Promise<string> {
  let body: unknown;
  try {
    body = await response.clone().json();
  } catch {
    try {
      body = await response.clone().text();
    } catch {
      body = undefined;
    }
  }
  return apiErrorMessage(body, { fallback, status: response.status });
}
